import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import classnames from 'classnames'
import PropTypes from 'prop-types'

import { getDashboardProgram, getProgramsDualPanelSelectorThunk } from '../../../actions/programActions'
import { getStudentsDualPanelSelector } from '../../../actions/usersActions'
import { addProgramEnrollmentsManually } from '../../../actions/enrollmentActions'
import {
  reset, selectProgramsData, selectStudentsData, setSearchValue, setUnavailableStudents,
} from '../../../reducers/dualPanelSelectorReducer'
import { resetDashboard } from '../../../reducers/programDashboardReducer'

import { categoryCompare } from '../../../utility/programHelper'

import Modal from '../../common/Modal'
import SubmitButton from '../../common/buttons/SubmitButton'
import DualPanelSelector from '../../common/dual-panel-selector/DualPanelSelector'
import ProgramCard from '../../common/dual-panel-selector/items/ProgramCard'
import CreateEnrollmentModal from '../../classes/program-dashboard-manager/add-students-modal/CreateEnrollmentModal'
import SuccessModal from '../../common/customModal/SuccessModal'

import { ReactComponent as FlagIcon } from '../../../assets/images/common/icons/Flag.svg'
import { ReactComponent as PersonIcon } from '../../../assets/images/common/icons/person-icon.svg'
import classes from './CreateEnrollmentWizard.module.scss'

import { DUAL_PANEL_SELECTABLE_TYPES, PER_PAGE } from '../../../constants/dual-panel-selector-constants'

const Placeholder = ({ icon, title, description }) => (
  <div className={classes.placeholder}>
    {icon}
    <h2 className={classes.title}>{title}</h2>
    <p className={classes.description}>{description}</p>
  </div>
)

Placeholder.propTypes = {
  icon: PropTypes.node,
  title: PropTypes.string,
  description: PropTypes.string,
}

// Step 1 — single-select program picker. Selection is held in the parent (local state), so the
// summary panel shows the chosen program and Continue stays disabled until one is picked.
const ProgramStep = ({ selectedProgram, onSelectProgram, onProceed, onClose, isLoading }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const programsData = useSelector(selectProgramsData)

  const fetchPrograms = useCallback((page, searchTerm, completed) =>
    dispatch(getProgramsDualPanelSelectorThunk({ page, perPage: PER_PAGE, searchTerm, completed }))
      .unwrap()
      .catch(() => toast.error(t('enrollmentsBilling.wizard.somethingWentWrong'), { position: toast.POSITION.TOP_RIGHT }))
  , [dispatch, t])

  const leftPanelProps = {
    tabTypes: [DUAL_PANEL_SELECTABLE_TYPES.programs],
    tabProps: {
      [DUAL_PANEL_SELECTABLE_TYPES.programs]: {
        titleText: t('enrollmentsBilling.wizard.selectProgramTitle'),
        allItems: programsData.items,
        withCompletedSwitcher: false,
        searchValue: programsData.searchValue,
        onSearchValueChange: (searchValue) => dispatch(setSearchValue({ searchValue, key: DUAL_PANEL_SELECTABLE_TYPES.programs })),
        getSelectedItem: (id) => selectedProgram?.id === id ? selectedProgram : undefined,
        fetchItems: fetchPrograms,
        pagination: programsData.pagination,
        onCheckChange: (checked, program) => onSelectProgram(checked ? null : program),
        itemCategoryTitle: (program) => program.category ? program.category.name : t('enrollmentsBilling.wizard.otherPrograms'),
        itemCategoryCompare: categoryCompare,
        isLoading: programsData.isLoading,
      },
    },
  }

  const rightPanelProps = {
    selectedItems: selectedProgram ? [selectedProgram] : [],
    selectedItemType: DUAL_PANEL_SELECTABLE_TYPES.programs,
    summaryTitle: t('enrollmentsBilling.wizard.selectedProgram'),
    summaryPlaceholder: (
      <Placeholder
        icon={<FlagIcon className={classes.icon} />}
        title={t('enrollmentsBilling.wizard.noProgramSelected')}
        description={t('enrollmentsBilling.wizard.noProgramSelectedDescription')}
      />
    ),
    onRemove: () => onSelectProgram(null),
  }

  return (
    <Modal className={classes.modal} title={t('enrollmentsBilling.wizard.createEnrollment')} isOpen onClose={onClose}>
      <div className={classnames('d-flex flex-column', classes.modalContentWrapper)}>
        <DualPanelSelector type='WaitlistProgramRoster' className={classes.dualPanelSelector} {...leftPanelProps} {...rightPanelProps} />
        <div className='mt-3 d-flex justify-content-end'>
          <SubmitButton
            disabled={!selectedProgram}
            loading={isLoading}
            className={classes.button}
            buttonMessage={t('enrollmentsBilling.wizard.continue')}
            onClick={onProceed}
          />
        </div>
      </div>
    </Modal>
  )
}

ProgramStep.propTypes = {
  selectedProgram: PropTypes.object,
  onSelectProgram: PropTypes.func.isRequired,
  onProceed: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
}

// Step 2 — multi-select student picker. Students already enrolled in (or dropped from) the chosen
// program are marked unavailable by the parent so they render greyed-out ("- Present") and can't be
// re-added. The right panel is headed by the chosen program's info (Figma 1932-14022). Selection is
// held in local parent state.
const StudentsStep = ({ program, communityId, selectedStudents, setSelectedStudents, onBack, onProceed, onClose }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const studentsData = useSelector(selectStudentsData)
  const selectedMap = useMemo(() => Object.fromEntries(selectedStudents.map(s => [s.id, s])), [selectedStudents])

  const fetchStudents = useCallback((page, searchTerm) =>
    dispatch(getStudentsDualPanelSelector({ page, perPage: PER_PAGE, communityId, searchTerm }))
      .unwrap()
      .catch(() => toast.error(t('enrollmentsBilling.wizard.somethingWentWrong'), { position: toast.POSITION.TOP_RIGHT }))
  , [communityId, dispatch, t])

  const toggleStudent = (checked, student) => {
    setSelectedStudents(current => checked
      ? current.filter(s => s.id !== student.id)
      : [...current, { ...student, type: DUAL_PANEL_SELECTABLE_TYPES.students }])
  }

  const leftPanelProps = {
    tabTypes: [DUAL_PANEL_SELECTABLE_TYPES.students],
    tabProps: {
      [DUAL_PANEL_SELECTABLE_TYPES.students]: {
        titleText: t('enrollmentsBilling.wizard.selectStudents', { count: selectedStudents.length }),
        allItems: studentsData.items,
        searchValue: studentsData.searchValue,
        onSearchValueChange: (searchValue) => dispatch(setSearchValue({ searchValue, key: DUAL_PANEL_SELECTABLE_TYPES.students })),
        getSelectedItem: (id) => selectedMap[id],
        fetchItems: fetchStudents,
        pagination: studentsData.pagination,
        onCheckChange: toggleStudent,
        isLoading: studentsData.isLoading,
      },
    },
  }

  const rightPanelProps = {
    selectedItems: selectedStudents,
    selectedItemType: DUAL_PANEL_SELECTABLE_TYPES.students,
    summaryHeader: <ProgramCard className={classes.programSummary} item={program} />,
    summaryPlaceholder: (
      <Placeholder
        icon={<PersonIcon className={classes.icon} />}
        title={t('enrollmentsBilling.wizard.noStudentsAdded')}
        description={t('enrollmentsBilling.wizard.noStudentsAddedDescription')}
      />
    ),
    onRemove: (student) => toggleStudent(true, student),
  }

  return (
    <Modal className={classes.modal} title={t('enrollmentsBilling.wizard.chooseStudents')} isOpen onClose={onClose}>
      <div className={classnames('d-flex flex-column', classes.modalContentWrapper)}>
        <DualPanelSelector type='ProgramRoster' className={classes.dualPanelSelector} {...leftPanelProps} {...rightPanelProps} />
        <div className='mt-3 d-flex justify-content-between'>
          <button type='button' className={classes.backButton} onClick={onBack}>{t('enrollmentsBilling.wizard.back')}</button>
          <SubmitButton
            disabled={!selectedStudents.length}
            className={classes.button}
            buttonMessage={t('enrollmentsBilling.wizard.continue')}
            onClick={onProceed}
          />
        </div>
      </div>
    </Modal>
  )
}

StudentsStep.propTypes = {
  program: PropTypes.object.isRequired,
  communityId: PropTypes.number.isRequired,
  selectedStudents: PropTypes.array.isRequired,
  setSelectedStudents: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onProceed: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
}

// Three-step Create Enrollment wizard for the standalone Enrollments & Billing page:
//   program  — pick the program
//   students — pick the participants
//   billing  — the shared CreateEnrollmentModal (create mode) to build the invoice + submit
// The program list items lack packages/coaches, so the full program is fetched between step 1 and 2.
const CreateEnrollmentWizard = ({ onClose, onCreated }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const communityId = useSelector(state => state.user.current_user.communities[0].id)

  const [step, setStep] = useState('program')
  const [selectedProgram, setSelectedProgram] = useState(null)
  const [fullProgram, setFullProgram] = useState(null)
  const [selectedStudents, setSelectedStudents] = useState([])
  const [loadingProgram, setLoadingProgram] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [successState, setSuccessState] = useState(null)

  useEffect(() => {
    dispatch(reset())
    // getDashboardProgram (used to load the chosen program) writes the shared programDashboard slice;
    // clear it on unmount so we don't leave a stale program behind for the program-dashboard route.
    return () => {
      dispatch(reset())
      dispatch(resetDashboard())
    }
  }, [dispatch])

  const goToStudents = () => {
    setLoadingProgram(true)
    dispatch(getDashboardProgram(selectedProgram.id)).unwrap()
      .then(({ course: program }) => {
        setFullProgram(program)
        // Enrolled (not-dropped) members come back on `enrollments`; previously-dropped members are
        // exposed separately as `droppedStudentIds`. Both are "already in" the program and can't be re-added.
        const enrolled = (program.enrollments || []).map(e => ({ id: e.user?.id ?? e.userId }))
        const dropped = (program.droppedStudentIds || []).map(id => ({ id }))
        dispatch(reset())
        dispatch(setUnavailableStudents([...enrolled, ...dropped]))
        setSelectedStudents([])
        setStep('students')
      })
      .catch(() => toast.error(t('enrollmentsBilling.wizard.somethingWentWrong'), { position: toast.POSITION.TOP_RIGHT }))
      .finally(() => setLoadingProgram(false))
  }

  const handleCreate = (payload) => {
    setSubmitting(true)
    dispatch(addProgramEnrollmentsManually({
      programId: fullProgram.id,
      userIds: selectedStudents.map(s => s.id),
      ...payload,
    })).unwrap()
      .then((response) => {
        if (response.enrollments?.skipped?.length > 0) {
          toast.warn(t('enrollmentsBilling.wizard.someStudentsNotEnrolled'), { position: toast.POSITION.TOP_RIGHT })
        }
        // Success confirmation modal (Figma 1932-14284). The email line only shows when the coach kept "Send as Email".
        setSuccessState({ emailSent: !!payload.sendEmail })
      })
      .catch(() => toast.error(t('enrollmentsBilling.wizard.somethingWentWrong'), { position: toast.POSITION.TOP_RIGHT }))
      .finally(() => setSubmitting(false))
  }

  const handleSuccessClose = () => {
    onCreated?.()
    onClose()
  }

  if (successState) {
    return (
      <SuccessModal
        isOpen
        onClose={handleSuccessClose}
        title={t('enrollmentsBilling.wizard.enrollmentsCreatedTitle')}
        message={successState.emailSent ? t('enrollmentsBilling.wizard.emailSentMessage') : ''}
        buttonMessage={t('enrollmentsBilling.wizard.close')}
      />
    )
  }

  if (step === 'program') {
    return (
      <ProgramStep
        selectedProgram={selectedProgram}
        onSelectProgram={setSelectedProgram}
        onProceed={goToStudents}
        onClose={onClose}
        isLoading={loadingProgram}
      />
    )
  }

  if (step === 'students') {
    return (
      <StudentsStep
        program={fullProgram}
        communityId={communityId}
        selectedStudents={selectedStudents}
        setSelectedStudents={setSelectedStudents}
        onBack={() => setStep('program')}
        onProceed={() => setStep('billing')}
        onClose={onClose}
      />
    )
  }

  return (
    <CreateEnrollmentModal
      isOpen
      mode='create'
      program={fullProgram}
      students={selectedStudents}
      onSubmit={handleCreate}
      onClose={onClose}
      isLoading={submitting}
    />
  )
}

CreateEnrollmentWizard.propTypes = {
  onClose: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
}

export default CreateEnrollmentWizard
